import { ImageZoom } from '../components/ImageZoom';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { quickReviewAPI, questionsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { HighlightableText } from '../components/HighlightableText';
import { ExplanationRenderer } from '../components/core/ExplanationRenderer';
import { SUBJECT_COLORS, cn } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { toast } from 'sonner';
import {
    Loader2,
    CheckCircle,
    XCircle,
    ArrowRight,
    Clock,
    Lightbulb,
    Trophy,
    Target,
    RotateCcw,
    ArrowLeft,
    Brain
} from 'lucide-react';
import Layout from '../components/Layout';

export default function QuickReview() {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    
    const [stats, setStats] = useState(null);
    const [sessionId, setSessionId] = useState(null);
    const [question, setQuestion] = useState(null);
    const [progress, setProgress] = useState({ current: 0, total: 0 });
    const [missCount, setMissCount] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingQuestion, setLoadingQuestion] = useState(false);
    const [starting, setStarting] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [result, setResult] = useState(null);
    const [strikethrough, setStrikethrough] = useState([]);
    const [startTime, setStartTime] = useState(null);
    const [elapsed, setElapsed] = useState(0);
    const [reviewComplete, setReviewComplete] = useState(false);
    const [correctCount, setCorrectCount] = useState(0);

    // Timer effect
    useEffect(() => {
        let interval;
        if (startTime && !submitted) {
            interval = setInterval(() => {
                setElapsed(Math.floor((Date.now() - startTime) / 1000));
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [startTime, submitted]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Fetch stats on mount
    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await quickReviewAPI.getStats();
            setStats(response.data);
        } catch (err) {
            console.error('Failed to fetch stats:', err);
            toast.error('Failed to load review stats');
        } finally {
            setLoading(false);
        }
    };

    const startReview = async () => {
        setStarting(true);
        try {
            const response = await quickReviewAPI.start();
            if (response.data.total_questions === 0) {
                toast.info('No missed questions to review!');
                return;
            }
            setSessionId(response.data.session_id);
            setProgress({ current: 0, total: response.data.total_questions });
            await fetchNextQuestion(response.data.session_id);
        } catch (err) {
            console.error('Failed to start review:', err);
            toast.error('Failed to start quick review');
        } finally {
            setStarting(false);
        }
    };

    const fetchNextQuestion = async (sid = sessionId) => {
        setLoadingQuestion(true);
        setSelectedAnswer(null);
        setSubmitted(false);
        setResult(null);
        setStrikethrough([]);
        setStartTime(null);
        setElapsed(0);

        try {
            const response = await quickReviewAPI.getQuestion(sid);
            
            if (response.data.status === 'completed') {
                setReviewComplete(true);
                toast.success('Quick review complete!');
            } else {
                setQuestion(response.data.question);
                setProgress(response.data.progress);
                setMissCount(response.data.miss_count || 1);
                setStartTime(Date.now());
            }
        } catch (err) {
            console.error('Failed to fetch question:', err);
            toast.error('Failed to load question');
        } finally {
            setLoadingQuestion(false);
        }
    };

    const submitAnswer = async () => {
        if (!selectedAnswer || !question) return;

        try {
            const response = await questionsAPI.submitAnswer(question.id, {
                question_id: question.id,
                selected_answer: selectedAnswer,
                time_spent_seconds: elapsed
            });
            
            setResult(response.data);
            setSubmitted(true);
            
            if (response.data.is_correct) {
                setCorrectCount(prev => prev + 1);
                toast.success('Correct! 🎉');
            } else {
                toast.error('Incorrect - review the explanation');
            }
        } catch (err) {
            console.error('Failed to submit answer:', err);
            const detail = err.response?.data?.detail || err.message || 'Unknown error';
            toast.error(`Failed to submit answer: ${detail}`);
        }
    };

    const handleNext = async () => {
        // Advance to next question
        await quickReviewAPI.next(sessionId);
        await fetchNextQuestion();
    };

    const toggleStrikethrough = (letter) => {
        if (submitted) return;
        setStrikethrough(prev => 
            prev.includes(letter) 
                ? prev.filter(l => l !== letter)
                : [...prev, letter]
        );
    };

    const getOptionStyle = (option) => {
        if (!submitted) {
            if (selectedAnswer === option.letter) {
                return isDark ? 'border-indigo-500 bg-indigo-900/40' : 'border-indigo-500 bg-indigo-50';
            }
            return isDark ? 'border-slate-600 hover:border-slate-500' : 'border-slate-200 hover:border-slate-300';
        }

        if (option.letter === result?.correct_answer) {
            return isDark ? 'border-emerald-500 bg-emerald-900/50' : 'border-emerald-500 bg-emerald-50';
        }
        if (selectedAnswer === option.letter && !result?.is_correct) {
            return isDark ? 'border-red-500 bg-red-900/50' : 'border-red-500 bg-red-50';
        }
        return isDark ? 'border-slate-700 opacity-60' : 'border-slate-200 opacity-60';
    };

    if (loading) {
        return (
            <Layout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-64" />
                    <Skeleton className="h-48 w-full" />
                </div>
            </Layout>
        );
    }

    // Show completion screen
    if (reviewComplete) {
        return (
            <Layout>
                <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="review-complete">
                    <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                        <CardContent className="p-8 text-center">
                            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                                <Trophy className="h-10 w-10 text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                                Quick Review Complete!
                            </h1>
                            <p className="text-slate-600 mb-6">
                                You've reviewed all your missed questions
                            </p>

                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="p-4 rounded-lg bg-white border border-slate-200">
                                    <p className="text-3xl font-bold text-emerald-600">{correctCount}</p>
                                    <p className="text-sm text-slate-500">Now Correct</p>
                                </div>
                                <div className="p-4 rounded-lg bg-white border border-slate-200">
                                    <p className="text-3xl font-bold text-indigo-600">{progress.total}</p>
                                    <p className="text-sm text-slate-500">Reviewed</p>
                                </div>
                            </div>

                            <div className="flex gap-3 justify-center">
                                <Button variant="outline" onClick={() => navigate('/study-planner')}>
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Study Planner
                                </Button>
                                <Button 
                                    onClick={() => {
                                        setReviewComplete(false);
                                        setSessionId(null);
                                        setCorrectCount(0);
                                        fetchStats();
                                    }}
                                    className="bg-gradient-to-r from-indigo-500 to-purple-500"
                                >
                                    <RotateCcw className="mr-2 h-4 w-4" />
                                    Review Again
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    // Show start screen if no session
    if (!sessionId) {
        return (
            <Layout>
                <div className="max-w-2xl mx-auto space-y-6 animate-fade-in" data-testid="quick-review-start">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                            Quick Review
                        </h1>
                        <p className="text-slate-500">Re-practice questions you've missed to strengthen weak areas</p>
                    </div>

                    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
                        <CardContent className="p-8 text-center">
                            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center">
                                <Brain className="h-8 w-8 text-white" />
                            </div>
                            
                            {stats && stats.total_missed > 0 ? (
                                <>
                                    <h2 className="text-xl font-bold text-slate-900 mb-4">
                                        {stats.total_missed} Questions to Review
                                    </h2>
                                    
                                    {/* Priority breakdown */}
                                    {stats.priority_breakdown && (
                                        <div className="grid grid-cols-3 gap-3 mb-6">
                                            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                                                <p className="text-2xl font-bold text-red-600">{stats.priority_breakdown.high || 0}</p>
                                                <p className="text-xs text-red-500">High Priority</p>
                                                <p className="text-xs text-slate-400">3+ misses</p>
                                            </div>
                                            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
                                                <p className="text-2xl font-bold text-amber-600">{stats.priority_breakdown.medium || 0}</p>
                                                <p className="text-xs text-amber-500">Medium</p>
                                                <p className="text-xs text-slate-400">2 misses</p>
                                            </div>
                                            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                                                <p className="text-2xl font-bold text-emerald-600">{stats.priority_breakdown.low || 0}</p>
                                                <p className="text-xs text-emerald-500">Low</p>
                                                <p className="text-xs text-slate-400">1 miss</p>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Subject breakdown */}
                                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                                        {Object.entries(stats.by_subject).map(([subject, count]) => (
                                            <Badge 
                                                key={subject}
                                                className={cn(
                                                    SUBJECT_COLORS[subject]?.bg || 'bg-slate-100',
                                                    SUBJECT_COLORS[subject]?.text || 'text-slate-700'
                                                )}
                                            >
                                                {subject}: {count}
                                            </Badge>
                                        ))}
                                    </div>
                                    
                                    <p className="text-sm text-slate-500 mb-4">
                                        Questions are sorted by priority - hardest ones first
                                    </p>

                                    <Button 
                                        size="lg"
                                        onClick={startReview}
                                        disabled={starting}
                                        className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
                                    >
                                        {starting ? (
                                            <>
                                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                                Starting...
                                            </>
                                        ) : (
                                            <>
                                                <RotateCcw className="mr-2 h-5 w-5" />
                                                Start Quick Review
                                            </>
                                        )}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <h2 className="text-xl font-bold text-slate-900 mb-2">
                                        No Missed Questions!
                                    </h2>
                                    <p className="text-slate-600 mb-6">
                                        Great job! You haven't missed any questions yet. Keep practicing!
                                    </p>
                                    <Button variant="outline" onClick={() => navigate('/study-planner')}>
                                        <ArrowLeft className="mr-2 h-4 w-4" />
                                        Back to Study Planner
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </Layout>
        );
    }

    // Show question
    return (
        <Layout>
            <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="quick-review-session">
                {/* Header with Progress */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Brain className="h-5 w-5 text-purple-500" />
                            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                                Quick Review
                            </h1>
                        </div>
                        <p className="text-sm text-slate-500">
                            Question {progress.current} of {progress.total}
                        </p>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-slate-600">
                            <Clock className="h-4 w-4" />
                            <span className="font-mono">{formatTime(elapsed)}</span>
                        </div>
                        <Badge className="bg-emerald-100 text-emerald-700">
                            {correctCount} Correct
                        </Badge>
                    </div>
                </div>

                {/* Progress Bar */}
                <Progress 
                    value={(progress.current / progress.total) * 100} 
                    className="h-2"
                />

                {/* Question Card */}
                {loadingQuestion ? (
                    <Card>
                        <CardContent className="p-8 text-center">
                            <Loader2 className="h-8 w-8 mx-auto mb-4 animate-spin text-purple-500" />
                            <p className="text-slate-600">Loading question...</p>
                        </CardContent>
                    </Card>
                ) : question ? (
                    <Card>
                        <CardHeader className="pb-4">
                            <div className="flex flex-wrap gap-2 mb-4">
                                <Badge className={cn(
                                    SUBJECT_COLORS[question.subject]?.bg || 'bg-slate-100',
                                    SUBJECT_COLORS[question.subject]?.text || 'text-slate-700'
                                )}>
                                    {question.subject}
                                </Badge>
                                {question.organ_system && (
                                    <Badge variant="outline">
                                        {question.organ_system}
                                    </Badge>
                                )}
                                <Badge className={cn(
                                    missCount >= 3 ? "bg-red-100 text-red-700" :
                                    missCount === 2 ? "bg-amber-100 text-amber-700" :
                                    "bg-slate-100 text-slate-700"
                                )}>
                                    {missCount >= 3 ? "🔥 " : missCount === 2 ? "⚠️ " : ""}
                                    Missed {missCount}x
                                </Badge>
                            </div>

                            {/* Vignette */}
                            <div className="prose prose-sm max-w-none">
                                <HighlightableText 
                                    text={question.vignette} 
                                    className="text-slate-700 leading-relaxed"
                                />
                            </div>

                            {/* Image if present */}
                            {question.image_base64 && (
                                <div className="mt-4 rounded-lg overflow-hidden border border-slate-200">
                                    <ImageZoom 
                                        src={`data:image/png;base64,${question.image_base64}`}
                                        alt="Medical illustration"
                                    />
                                </div>
                            )}

                            {/* Question Stem */}
                            <CardTitle className="text-lg mt-4" style={{ fontFamily: 'Manrope' }}>
                                {question.question_stem}
                            </CardTitle>
                        </CardHeader>

                        <CardContent className="space-y-3">
                            {/* Options */}
                            {question.options?.map((option) => (
                                <button
                                    key={option.letter}
                                    onClick={() => !submitted && setSelectedAnswer(option.letter)}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        toggleStrikethrough(option.letter);
                                    }}
                                    disabled={submitted}
                                    className={cn(
                                        "w-full text-left p-4 rounded-lg border-2 transition-all",
                                        getOptionStyle(option),
                                        strikethrough.includes(option.letter) && !submitted && "opacity-40 line-through"
                                    )}
                                >
                                    <div className="flex items-start gap-3">
                                        <span className={cn(
                                            "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm",
                                            selectedAnswer === option.letter && !submitted
                                                ? "bg-indigo-500 text-white"
                                                : submitted && option.letter === result?.correct_answer
                                                    ? "bg-emerald-500 text-white"
                                                    : submitted && selectedAnswer === option.letter && !result?.is_correct
                                                        ? "bg-red-500 text-white"
                                                        : isDark ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"
                                        )}>
                                            {option.letter}
                                        </span>
                                        <span className={cn(
                                            "flex-1 pt-1",
                                            !submitted && isDark && "text-slate-200",
                                            submitted && option.letter === result?.correct_answer && !isDark && "text-emerald-900",
                                            submitted && option.letter === result?.correct_answer && isDark && "text-emerald-200",
                                            submitted && selectedAnswer === option.letter && !result?.is_correct && !isDark && "text-red-900",
                                            submitted && selectedAnswer === option.letter && !result?.is_correct && isDark && "text-red-200"
                                        )}>{option.text}</span>
                                        {submitted && option.letter === result?.correct_answer && (
                                            <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />
                                        )}
                                        {submitted && selectedAnswer === option.letter && !result?.is_correct && (
                                            <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                                        )}
                                    </div>
                                </button>
                            ))}

                            {/* Explanation */}
                            {submitted && result && (
                                <div className={cn(
                                    "mt-6 p-4 rounded-lg",
                                    result.is_correct && !isDark && "bg-emerald-100 border border-emerald-500",
                                    result.is_correct && isDark && "bg-emerald-900/50 border border-emerald-500",
                                    !result.is_correct && !isDark && "bg-amber-100 border border-amber-500",
                                    !result.is_correct && isDark && "bg-amber-900/50 border border-amber-500"
                                )}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <Lightbulb className={cn(
                                            "h-5 w-5",
                                            result.is_correct && "text-emerald-500",
                                            !result.is_correct && "text-amber-500"
                                        )} />
                                        <span className={cn(
                                            "font-semibold",
                                            result.is_correct && !isDark && "text-emerald-800",
                                            result.is_correct && isDark && "text-emerald-300",
                                            !result.is_correct && !isDark && "text-amber-800",
                                            !result.is_correct && isDark && "text-amber-300"
                                        )}>Explanation</span>
                                    </div>
                                    <ExplanationRenderer
                                        text={result.explanation || question.explanation || "Review this concept carefully."}
                                        className={cn(
                                            "text-sm",
                                            result.is_correct && !isDark && "text-emerald-900",
                                            result.is_correct && isDark && "text-emerald-200",
                                            !result.is_correct && !isDark && "text-amber-900",
                                            !result.is_correct && isDark && "text-amber-200"
                                        )}
                                    />
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-end gap-3 pt-4">
                                {!submitted ? (
                                    <Button
                                        onClick={submitAnswer}
                                        disabled={!selectedAnswer}
                                        className="bg-indigo-600 hover:bg-indigo-700"
                                    >
                                        Submit Answer
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleNext}
                                        className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
                                    >
                                        {progress.current >= progress.total ? 'Finish Review' : 'Next Question'}
                                        <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ) : null}

                {/* Tips */}
                <p className="text-xs text-slate-400 text-center">
                    Tip: Right-click on an option to strike it through
                </p>
            </div>
        </Layout>
    );
}
